"use client";
import "./contactPage.css";
import { useEffect, useRef, useState } from "react";
import ContactForm from "src/components/Common/ContactForm";
import { contactItems } from "src/utils/mock/contactValidation";
import ContactHeroSection from "src/components/molecules/ContactUsSetions/ContactHeroSection";

export default function ContactUsComponent() {
  const wrapperRef = useRef(null);
  const contentRef = useRef(null);
  const heroRef = useRef(null);
  const marqueeRef = useRef(null);
  const cardsRef = useRef(null);
  const formRef = useRef(null);

  const [sent, setSent] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    let ctx;
    const initGSAP = async () => {
      try {
        const { gsap } = await import("gsap");
        const { ScrollTrigger } = await import("gsap/ScrollTrigger");
        const { ScrollSmoother } = await import("gsap/ScrollSmoother");
        const { SplitText } = await import("gsap/SplitText");

        gsap.registerPlugin(ScrollTrigger, ScrollSmoother, SplitText);

        ctx = gsap.context(() => {
          if (heroRef.current) {
            const titleEl = heroRef.current.querySelector(".hero-title");
            const subEl = heroRef.current.querySelector(".hero-sub");
            const split = new SplitText(titleEl, { type: "chars,words" });

            gsap.fromTo(
              split.chars,
              { opacity: 0, y: 60, rotateX: -40 },
              {
                opacity: 1,
                y: 0,
                rotateX: 0,
                duration: 0.9,
                ease: "back.out(1.5)",
                stagger: 0.025,
                delay: 0.1,
              },
            );

            gsap.fromTo(
              subEl,
              { opacity: 0, y: 24 },
              {
                opacity: 1,
                y: 0,
                duration: 0.8,
                ease: "power3.out",
                delay: 0.6,
              },
            );

            gsap.to(heroRef.current.querySelector(".blob-a"), {
              y: -120,
              x: 60,
              scrollTrigger: {
                trigger: heroRef.current,
                start: "top top",
                end: "bottom top",
                scrub: 1.5,
              },
            });
            gsap.to(heroRef.current.querySelector(".blob-b"), {
              y: -80,
              x: -40,
              scrollTrigger: {
                trigger: heroRef.current,
                start: "top top",
                end: "bottom top",
                scrub: 2,
              },
            });
          }

          if (marqueeRef.current) {
            gsap.to(marqueeRef.current.querySelector(".marquee-inner"), {
              x: "-50%",
              ease: "none",
              scrollTrigger: {
                trigger: marqueeRef.current,
                start: "top bottom",
                end: "bottom top",
                scrub: 1,
              },
            });
          }

          if (cardsRef.current) {
            gsap.fromTo(
              cardsRef.current.querySelectorAll(".contact-card"),
              { opacity: 0, y: 50, scale: 0.96 },
              {
                opacity: 1,
                y: 0,
                scale: 1,
                duration: 0.7,
                ease: "power3.out",
                stagger: 0.12,
                scrollTrigger: { trigger: cardsRef.current, start: "top 80%" },
              },
            );
          }

          if (formRef.current) {
            gsap.fromTo(
              formRef.current.querySelector(".form-info-col"),
              { opacity: 0, x: -50 },
              {
                opacity: 1,
                x: 0,
                duration: 0.85,
                ease: "power3.out",
                scrollTrigger: { trigger: formRef.current, start: "top 75%" },
              },
            );
            gsap.fromTo(
              formRef.current.querySelector(".form-card"),
              { opacity: 0, x: 50, y: 20 },
              {
                opacity: 1,
                x: 0,
                y: 0,
                duration: 0.85,
                ease: "power3.out",
                delay: 0.1,
                scrollTrigger: { trigger: formRef.current, start: "top 75%" },
              },
            );

            gsap.fromTo(
              formRef.current.querySelectorAll(".field-line"),
              { scaleX: 0 },
              {
                scaleX: 1,
                duration: 0.6,
                ease: "power3.out",
                stagger: 0.08,
                scrollTrigger: { trigger: formRef.current, start: "top 70%" },
              },
            );
          }
        });
      } catch (e) {
        console.warn("GSAP not available");
      }
    };

    initGSAP();
    return () => ctx?.revert();
  }, []);

  return (
    <div id="smooth-wrapper" ref={wrapperRef} className="contact-smooth-wrapper">
      <div id="smooth-content" ref={contentRef} className="contact-smooth-content">
        <ContactHeroSection />
        <section className="contact-main-grid-section">
          <div className="container">
            <div className="ui-horizontal-row top-row">
              <div ref={cardsRef} className="info-cards-column">
                {contactItems.map((item) => (
                  <a key={item.label} href={item.href} className="ui-contact-card" target={item.type === "location" ? "_blank" : undefined}>
                    <div className="ui-card-icon-wrap">
                      <img src={item.img} alt={item.label} className="sidebar-img-icon" />
                    </div>
                    <div className="ui-card-content">
                      <span className="ui-label">{item.label}</span>
                      <strong className="ui-value">{item.value}</strong>
                      {item.sub && <span className="ui-sub">{item.sub}</span>}
                    </div>
                  </a>
                ))}
              </div>

              <div ref={formRef} className="ui-form-card">
                {sent ? (
                  <div className="success-state">
                    <div className="success-icon">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="80"
                        height="80"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                        <polyline points="22 4 12 14.01 9 11.01" />
                      </svg>
                    </div>

                    <h3 className="success-title">Message Sent Successfully!</h3>
                    <p className="success-subtitle">
                      Thank you for reaching out. Our team has received your message and will get back to you within <strong>24 hours</strong>.
                    </p>

                    <button onClick={() => setSent(false)} className="success-reset-btn">
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <ContactForm sent={sent} setSent={setSent} />
                )}
              </div>
            </div>

            <div className="ui-horizontal-row bottom-row">
              <div className="map-wrapper relative overflow-hidden rounded-md backdrop-blur-xl">
                <iframe
                  height="100%"
                  width="100%"
                  title="maps"
                  className="map-iframe"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3502.0200064422106!2d77.37764375089314!3d28.629162482332816!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390ce40a1a48d491%3A0x773396a1d869a95f!2sTycho%20Technologies%20Pvt.%20Ltd.!5e0!3m2!1sen!2sus!4v1637126209735!5m2!1sen!2sus"
                  style={{
                    border: 0,
                  }}
                />

                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-full pointer-events-none">
                  <img src="/images/Tycho_Logo_new.png" alt="Location" className="w-13 h-13" />
                </div>
              </div>

              <div className="why-choose-us-ui">
                <div className="why-header">
                  <h3>Why Businesses Choose Tycho</h3>
                  <p>We go beyond development – we build long-term digital partnerships.</p>
                </div>
                <div className="features-grid">
                  {[
                    {
                      t: "Quick Response",
                      d: "We reply within 24 business hours",
                      img: "/images/contact/QUICKRESPONSE.webp",
                    },
                    {
                      t: "Experienced Team",
                      d: "50+ experts with proven experience",
                      img: "/images/contact/ExperiencedTeam.webp",
                    },
                    {
                      t: "On-Time Delivery",
                      d: "Agile process for on-time delivery",
                      img: "/images/contact/onntimedelievery.webp",
                    },
                    {
                      t: "Transparent Process",
                      d: "Clear communication at every step",
                      img: "/images/contact/transparentprocess.webp",
                    },
                    {
                      t: "Secure & Reliable",
                      d: "Data security and NDA guaranteed",
                      img: "/images/contact/securereliable.webp",
                    },
                    {
                      t: "Client-Centric Approach",
                      d: "Your success is our priority",
                      img: "/images/contact/clientapproach.webp",
                    },
                  ].map((feat, i) => (
                    <div key={i} className="feature-item">
                      <div className="feature-icon-box">
                        <img src={feat.img} alt={feat.t} className="feature-img-icon" />
                      </div>
                      <div className="feature-text">
                        <h4>{feat.t}</h4>
                        <p>{feat.d}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
      <style jsx>{`
        /* Dark mode support */
        :global([data-theme="dark"]) .success-icon {
          background: rgba(74, 222, 128, 0.15);
        }
        :global([data-theme="dark"]) .map-iframe {
          filter: grayscale(1) invert(1) hue-rotate(200deg) saturate(0.1) brightness(1.4) contrast(1);
        }
      `}</style>
    </div>
  );
}
